// src/components/layout/AdminSidebar.jsx

import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/guard/AuthProvider';
import sidebarMenusData from './sidebarMenusData';

/**
 * Return true if the current user (via permissions + isSuperAdmin)
 * should see a menu item guarded by `menuPerm`.
 */
function canSee(menuPerm: string, userPerms: string[], isSuperAdmin: boolean) {
  if (isSuperAdmin) return true;     // super-admin always sees everything
  if (!menuPerm)     return true;     // no permission required

  // direct match
  if (userPerms.includes(menuPerm)) return true;

  // if user has the “_all” version they also get the base:
  //   e.g. menuPerm = 'view_attendance' & user has 'view_attendance_all'
  if (userPerms.includes(`${menuPerm}_all`)) return true;

  // if menuPerm _is_ the “_all” version, allow the base as well:
  //   e.g. menuPerm = 'view_attendance_all' & user has 'view_attendance'
  if (menuPerm.endsWith('_all')) {
    const base = menuPerm.replace(/_all$/, '');
    if (userPerms.includes(base)) return true;
  }

  return false;
}

export default function AdminSidebar() {
  const { permissions = [], isSuperAdmin } = useContext(AuthContext);

  return (
    <aside className="db-sidebar">
      <div className="db-sidebar-header">
        <button className="fa-solid fa-xmark xmark-btn close-db-menu" />
      </div>

      {sidebarMenusData.length > 0 && (
        <nav className="db-sidebar-nav">
          <ul className="db-sidebar-nav-list">
            {sidebarMenusData
              .filter(item =>
                canSee(item.permission_name, permissions, isSuperAdmin)
              )
              .map(menu => (
                <React.Fragment key={menu.id}>
                  {menu.url === '#' ? (
                    <li className="db-sidebar-nav-item">
                      <a
                        href="javascript:void(0);"
                        className="db-sidebar-nav-title"
                      >
                        {menu.text}
                      </a>
                    </li>
                  ) : (
                    <li className="db-sidebar-nav-item">
                      <Link
                        to={`/admin/${menu.url}`}
                        className="db-sidebar-nav-menu"
                      >
                        <i className={`text-sm ${menu.icon}`}></i>
                        <span className="text-base flex-auto">
                          {menu.text}
                        </span>
                      </Link>
                    </li>
                  )}

                  {menu.children &&
                    menu.children
                      .filter(child =>
                        canSee(child.permission_name, permissions, isSuperAdmin)
                      )
                      .map(child => (
                        <li className="db-sidebar-nav-item" key={child.id}>
                          <Link
                            to={`/admin/${child.url}`}
                            className="db-sidebar-nav-menu"
                          >
                            <i className={`text-sm ${child.icon}`}></i>
                            <span className="text-base flex-auto">
                              {child.text}
                            </span>
                          </Link>
                        </li>
                      ))}
                </React.Fragment>
              ))}
          </ul>
        </nav>
      )}
    </aside>
  );
}
